// @ts-nocheck
/**
 * @function SwalToast
 * @description Displays a toast using the Snackbar component from the Material-UI library.
 * This function is implemented as an Immediately Invoked Function Expression (IIFE)
 * to encapsulate the `activeToasts` set and prevent duplicate toasts.
 *
 * @note The IIFE ensures that `activeToasts` maintains its state across multiple function calls
 * without polluting the global scope, making it an effective way to track active toast IDs.
 *
 * @param {object} props - The props object.
 * @param {string} [props.icon='success'] - The type of the toast icon. Defaults to 'success'.
 * @param {string} props.title - The title of the toast.
 * @param {string} [props.id] - The unique ID of the toast to prevent duplication.
 * @param {number} [props.autoHideDuration=180000] - The duration before the toast hides automatically.
 *
 * @returns {void}
 */
const SwalToast = (() => {
  // Track active toast IDs to prevents duplicate toasts with the same ID.
  const activeToasts = new Set();

  return ({ icon = 'success', title, id, autoHideDuration = 180000 }) => {
    // Validate icon type
    const validIcons = ['success', 'error', 'info', 'warning'];
    const toastType = validIcons.includes(icon) ? icon : 'info';

    // Prevent duplicate toasts if `id` is provided
    if (id && activeToasts.has(id)) {
      return;
    }

    // Add the ID to the active toasts set
    if (id) {
      activeToasts.add(id);
    }

    window.enqueueSnackbar(title, {
      variant: toastType,
      preventDuplicate: true,
      autoHideDuration,
      onClose: () => {
        // Remove the ID from the active toasts set on close
        if (id) {
          activeToasts.delete(id);
        }
      },
    });

    // Update the live region for screen readers
    const liveRegion = document.getElementById('snackbar-live-region');
    if (liveRegion) {
      // Clear the live region first (to ensure re-announcement)
      liveRegion.textContent = '';
      setTimeout(() => {
        liveRegion.textContent = title;
      }, 100); // Small delay to ensure content is read
    }
  };
})();

export default SwalToast;
